import React, { useState, useEffect } from "react";
import { Form, Button, Alert, Row, Col, Spinner } from "react-bootstrap";
import {
  patchBusinessKycRequest,
  postBusinessKycRequest,
  getBusinessTypes,
  getBusinessSegments,
  getIndustrySectors,
  getCities,
} from "@/services";
import { useParams } from "react-router-dom";

const BusinessKYCForm = ({ loanDetails, business, goToNextStep, reloadBusinessKyc, isKycReadonly, userData }) => {
  const { applicationId: applicationUuid } = useParams();
  const isNewLoan = loanDetails?.loan_type === 'new';
  const [formData, setFormData] = useState({
    loan_id: applicationUuid,
    name: business?.name || userData?.company_details?.company_name || "",
    identity: business?.identity || '',
    business_type: business?.business_type || "",
    business_segment: business?.business_segment || "",
    industry_sector: business?.industry_sector || userData?.company_details?.company_type?.value || "",
    address: business?.address || "",
    city: business?.city || "",
    postal_code: business?.postal_code || "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(true);
  // dropdown data from APIs
  const [companyTypes, setCompanyTypes] = useState([]);
  const [businessSegments, setBusinessSegments] = useState([]);
  const [industrySectors, setIndustrySectors] = useState([]);
  const [cities, setCities] = useState([]);

  // fetch dropdown data only once
  useEffect(() => {
    const fetchInitialOptions = async () => {
      setLoadingOptions(true);
      try {
        const [typesRes, sectorsRes, citiesRes, businessSegmentsRes] = await Promise.all([
          getBusinessTypes(),
          getIndustrySectors(),
          getCities(),
          getBusinessSegments()
        ]);

        setCompanyTypes(
          (typesRes?.results || []).map((t) => ({
            value: t.id,
            label: t.name,
          }))
        );
        setBusinessSegments((businessSegmentsRes?.results || []).map(s => ({
          value: s.id,
          label: s.name,
        })));
        setIndustrySectors(
          (sectorsRes?.results || []).map((t) => ({
            value: t.id,
            label: t.name,
          }))
        );
        setCities((citiesRes?.results || []).map((t) => ({
          value: t.id,
          label: t.name,
        })));
        setLoadingOptions(false);
      } catch (err) {
        console.error("Failed to fetch dropdown options", err);
        setErrors({
          general: "Failed to load KYC options. Please refresh.",
        });
      }
    };

    fetchInitialOptions();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isKycReadonly) {
      if (goToNextStep) goToNextStep()
    } else {
      setSubmitting(true);
      setErrors({});

      const newErrors = {};
      if (!formData.name) newErrors.name = "Business name is required";
      if (!formData.identity) newErrors.identity = "Business Identity is required";
      if (!formData.business_type)
        newErrors.business_type = "Business type is required";
      if (!formData.business_segment)
        newErrors.business_segment = "Business segment is required";
      if (!formData.industry_sector)
        newErrors.industry_sector = "Industry sector is required";
      if (!formData.address) newErrors.address = "Company address is required";
      if (!formData.city) newErrors.city = "City is required";
      if (!formData.postal_code) newErrors.postal_code = "PO Box is required";

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        setSubmitting(false);
        return;
      }
      try {
        let res;
        if (business?.uuid) {
          res = await patchBusinessKycRequest(business?.uuid, formData);
        } else {
          res = await postBusinessKycRequest(formData);
        }

        if (res?.success === false) {
          setErrors(res.error || { general: res.message || "Something went wrong" });
          return;
        }
        await reloadBusinessKyc();
        if (goToNextStep) goToNextStep();
      } catch (err) {
        setErrors({
          general: "Failed to submit Business KYC. Try again.",
        });
      } finally {
        setSubmitting(false);
      }
    }
  };

  if (loadingOptions) {
    return (
      <div className="d-flex justify-content-center align-items-center p-5">
        <Spinner animation="border" variant="primary" />
        <span className="ms-2">Loading KYC options...</span>
      </div>
    );
  }

  return (
    <Form onSubmit={handleSubmit} className="mb-4 w-full">
      {errors.general && <Alert variant="danger">{errors.general}</Alert>}
      <fieldset disabled={isKycReadonly}>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3" controlId="business-kyc-name">
              <Form.Label>
                Business Name <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                name="name"
                disabled={!isNewLoan}
                value={formData.name}
                onChange={handleChange}
                isInvalid={!!errors.name}
              />
              <Form.Control.Feedback type="invalid">
                {errors.name}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3" controlId="business-kyc-identity">
              <Form.Label>
                Trade License Number <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                name="identity"
                disabled={!isNewLoan}
                value={formData.identity}
                onChange={handleChange}
                isInvalid={!!errors.identity}
                data-testid="fincobox-kyc-trade-license"
              />
              <Form.Control.Feedback type="invalid">
                {errors.identity}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3" controlId="business-kyc-business_type">
              <Form.Label>
                Business Type <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                name="business_type"
                value={formData.business_type}
                onChange={handleChange}
                isInvalid={!!errors.business_type}
                data-testid="fincobox-kyc-business-type"
              >
                <option value="">-- Select Business Type --</option>
                {companyTypes.map((type) => (
                  <option key={type.value} value={type.value} data-option-value={type.value}>
                    {type.label}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {errors.business_type}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group className="mb-3" controlId="business-kyc-business_segment">
              <Form.Label>
                Business Segment <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                name="business_segment"
                value={formData.business_segment}
                onChange={handleChange}
                isInvalid={!!errors.business_segment}
                data-testid="fincobox-kyc-business-segment"
              >
                <option value="">-- Select Segment --</option>
                {businessSegments.map((seg) => (
                  <option key={seg.value} value={seg.value} data-option-value={seg.value}>
                    {seg.label}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {errors.business_segment}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>

        <Form.Group className="mb-3" controlId="business-kyc-industry_sector">
          <Form.Label>
            Industry Sector <span className="text-danger">*</span>
          </Form.Label>
          <Form.Select
            name="industry_sector"
            value={formData.industry_sector}
            onChange={handleChange}
            isInvalid={!!errors.industry_sector}
            data-testid="fincobox-kyc-industry-sector"
          >
            <option value="">-- Select Industry Sector --</option>
            {industrySectors.map((sector) => (
              <option key={sector.value} value={sector.value} data-option-value={sector.value}>
                {sector.label}
              </option>
            ))}
          </Form.Select>
          <Form.Control.Feedback type="invalid">
            {errors.industry_sector}
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3" controlId="business-kyc-address">
          <Form.Label>
            Company Address <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            name="address"
            value={formData.address}
            onChange={handleChange}
            isInvalid={!!errors.address}
            data-testid="fincobox-kyc-address"
          />
          <Form.Control.Feedback type="invalid">
            {errors.address}
          </Form.Control.Feedback>
        </Form.Group>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3" controlId="business-kyc-city">
              <Form.Label>
                City <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                name="city"
                value={formData.city}
                onChange={handleChange}
                isInvalid={!!errors.city}
                data-testid="fincobox-kyc-city"
              >
                <option value="">-- Select City --</option>
                {cities.map((type) => (
                  <option key={type.value} value={type.value} data-option-value={type.value}>
                    {type.label}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {errors.city}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group className="mb-3" controlId="business-kyc-postal_code">
              <Form.Label>
                PO Box <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                name="postal_code"
                value={formData.postal_code}
                onChange={handleChange}
                isInvalid={!!errors.postal_code}
                data-testid="fincobox-kyc-po-box"
              />
              <Form.Control.Feedback type="invalid">
                {errors.postal_code}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>
      </fieldset>
      <div className="d-flex justify-content-end gap-2 mt-4">
        <Button id="b_submit_business_kyc_form" type="submit" variant="primary" disabled={submitting} style={{ minWidth: '240px' }}>
          {submitting ? "Saving..." : isKycReadonly ? "Continue" : "Save & Continue"}
        </Button>
      </div>
    </Form>
  );
};

export default BusinessKYCForm;
